import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ENVIRONMENT_KEYS, ROUTES } from 'shared/constants';
import { ORGANIZATION_SELECTION_STATUS } from 'shared/constants/organization-selection.constants';
import { currentEnvironment } from 'shared/services/Environment';
import {
  getOrganizationSelectionStatus,
  isOrganizationSelectionPending,
} from 'shared/store/selectors/available-organizations.selector';
import { Loader } from 'src/components/core';
import { setPostLoginRedirect } from 'src/utils/post-login-redirect';
import { usePostLoginRedirect } from 'src/hooks/use-post-login-redirect';

// Holds the private layouts behind the organization selector. Wraps the layout
// (not inside it) so a pending gate never flashes the app chrome; the intended
// route is stashed so choosing an org lands there, not on the dashboard.
export const OrganizationGate = ({ children }) => {
  const status = useSelector(getOrganizationSelectionStatus);
  const location = useLocation();
  const isPending = status === ORGANIZATION_SELECTION_STATUS.PENDING;
  const stashedRef = React.useRef(false);

  // Stash once per transition into PENDING — a render-phase write would survive
  // discarded renders and re-fire every render.
  React.useEffect(() => {
    if (!isPending) {
      stashedRef.current = false;
      return;
    }
    if (stashedRef.current) return;
    stashedRef.current = true;
    setPostLoginRedirect(location.pathname + location.search);
  }, [isPending, location]);

  if (isPending) {
    return <Navigate to={ROUTES.SELECT_ORGANIZATION} state={{ from: location }} replace />;
  }

  // Rendering the app mid-decision would flash the wrong organization's data.
  if (status === ORGANIZATION_SELECTION_STATUS.RESOLVING) {
    return <Loader />;
  }

  return <>{children}</>;
};

// The selector page's own guard — it lives outside OrganizationGate (which would
// redirect it to itself): unreachable unless the gate is up, and returns the
// user to the stashed route after picking. Outside production, ?preview=1
// renders the page with the gate down.
export const OrganizationSelectorRoute = ({ element }) => {
  const isPending = useSelector(isOrganizationSelectionPending);
  const location = useLocation();
  const target = usePostLoginRedirect(!isPending, ROUTES.HOME);

  const isPreview =
    currentEnvironment() !== ENVIRONMENT_KEYS.PRODUCTION &&
    new URLSearchParams(location.search).get('preview') === '1';

  return isPending || isPreview ? element : <Navigate to={target} replace />;
};
